import {
  EMPTY_FIELD_MESSAGE,
  validateEditorEn,
  validateSpace,
  validateSpaceTextInput,
  validateTextEn,
  validateUrl,
} from '@Utils/validation';
import { climateChangeGovernanceType } from 'src/Enum/EsgGreenhouse';
import { z } from 'zod';
import { modalAddSubCommitteesValidationSchema } from './ClimateChangeStructureSubcommittee/ModalSubcommittees/validator';

enum CLIMATE_CHANGE_STRUCTURE_MESSAGE {
  COMMITTEE = 'กรุณาเลือกคณะกรรมการอย่างน้อย 1 ตัวเลือก',
  INTRODUCTION_EN = 'กรุณาระบุคำบรรยายเป็นภาษาอังกฤษ',
  REMARK_EN = 'กรุณาระบุหมายเหตุเป็นภาษาอังกฤษ',
}

enum CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE {
  SUBCOMMITTEE_DETAILS = 'กรุณาเพิ่มข้อมูลกรรมการชุดย่อยที่มีบทบาทในการกำกับดูแลด้านการเปลี่ยนแปลงสภาพภูมิอากาศ',
  DESCRIPTION = 'กรุณาระบุคำอธิบายบทบาทในการกำกับดูแลด้านการเปลี่ยนแปลงสภาพภูมิอากาศของคณะกรรมการบริษัท',
  DESCRIPTION_EN = 'กรุณาระบุคำอธิบายบทบาทในการกำกับดูแลด้านการเปลี่ยนแปลงสภาพภูมิอากาศของคณะกรรมการบริษัทเป็นภาษาอังกฤษ',
  INVALID_URL = 'กรุณาระบุ URL ที่ถูกต้อง',
  CLIMATE_CHANGE_GOVERNANCE = 'บริษัทต้องมีการกำกับดูแลด้านการเปลี่ยนแปลงสภาพภูมิอากาศ จึงสามารถทำ Corporate Value Up Plan ได้',
}

const IntroductionSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateEditorEn, {
      message: CLIMATE_CHANGE_STRUCTURE_MESSAGE.INTRODUCTION_EN,
    })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

const RemarkSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateTextEn, {
      message: CLIMATE_CHANGE_STRUCTURE_MESSAGE.REMARK_EN,
    })
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

const CommitteeSchema = z
  .object({
    subcommitteeResponsibility: z.boolean().nullable(),
    boardResponsibility: z.boolean().nullable(),
  })
  .refine((committee) => Object.values(committee).some((value) => value), {
    message: CLIMATE_CHANGE_STRUCTURE_MESSAGE.COMMITTEE,
  });

const BoardResponsibilitySchema = z.object({
  remark: RemarkSchema,
  descriptionTh: z
    .string({
      message: CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE.DESCRIPTION,
    })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  descriptionEn: z
    .string({ message: CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE.DESCRIPTION })
    .regex(validateEditorEn, {
      message: CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE.DESCRIPTION_EN,
    })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  policyUrlTh: z
    .string()
    .regex(validateUrl, {
      message: CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE.INVALID_URL,
    })
    .optional()
    .or(z.literal(''))
    .nullable(),
  policyUrlEn: z
    .string()
    .regex(validateUrl, {
      message: CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE.INVALID_URL,
    })
    .optional()
    .or(z.literal(''))
    .nullable(),
  policyPageTh: z.string().nullable().optional(),
  policyPageEn: z.string().nullable().optional(),
});

const SubcommitteeResponsibilitySchema = z.object({
  remark: RemarkSchema,
  subcommittees: z
    .array(modalAddSubCommitteesValidationSchema, {
      message: CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE.SUBCOMMITTEE_DETAILS,
    })

    .nonempty({
      message: CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE.SUBCOMMITTEE_DETAILS,
    })
    .refine(
      (subcommittees) => {
        const hasClimateChangeGovernance = subcommittees.some(
          (subcommittee) => {
            return (
              Array.isArray(subcommittee.subcommitteeRoles) &&
              subcommittee.subcommitteeRoles.some(
                (role) =>
                  role.infoCategoryMasterDataId ===
                  climateChangeGovernanceType.CLIMATE_CHANGE_GOVERNANCE
              )
            );
          }
        );
        return hasClimateChangeGovernance;
      },
      {
        message: CLIMATE_CHANGE_STRUCTURE_BR_MESSAGE.CLIMATE_CHANGE_GOVERNANCE,
      }
    ),
});

const CompletedSectionSchema = z
  .object({
    isCompleted: z.boolean(),
    completionDate: z.date().nullable().optional(),
  })
  .nullable()
  .optional();

export const ClimateChangeStructureSchema = z.object({
  introduction: IntroductionSchema,
  remark: RemarkSchema,
  committee: CommitteeSchema,
  boardResponsibility: BoardResponsibilitySchema,
  subcommitteeResponsibility: SubcommitteeResponsibilitySchema,
  completedSection: CompletedSectionSchema,
});
